import {Injectable} from "@angular/core";
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse} from "@angular/common/http";
import {catchError, map, mergeMap, Observable, of, throwError} from "rxjs";
import {RequestType} from "../enums/RequestType";
import {ResponseStatusCode} from "../enums/ResponseStatusCode";
import {ErrorHandlerService} from "../classes/APIErrorHandler";
import {JWTToken} from "../interfaces/JWTToken";
import {ApiType} from "../enums/api-type";
import {environment} from "../../../environments/environment";
import {ValidationErrorType} from "../enums/ValidationErrorType";
import { AuthService } from "../../website/services/auth.service";


@Injectable({
  providedIn: "root",
})
export class BaseApiService {
  baseUrl = environment.apiUrl
  version = "v1"
  private jwtToken: JWTToken | null
  private refreshTokenTimer: ReturnType<typeof setTimeout>;

  constructor(
    private httpClient: HttpClient,
    private errorHandlerService: ErrorHandlerService,
  ) {
    this.getToken()
  }

  call<REQUEST, RESPONSE>(parameters: {
    apiType: ApiType,
    pathVariables?: string[]
    body?: REQUEST | null
    getParameters?: string
    dataUrl?: string
    listDataUrl?: string[]
    requestType: RequestType,
  }): Observable<RESPONSE> {
    if (parameters.pathVariables === undefined) parameters.pathVariables = []
    if (parameters.body === undefined) parameters.body = null;
    if (parameters.listDataUrl === undefined) parameters.listDataUrl = [];

    let url: string = `${this.baseUrl}/${this.version}/${parameters.apiType}`

    if (parameters.listDataUrl.isNotEmpty()) {
      url = url.format(...parameters.listDataUrl)
    } else {
      url = url.replace(/{}/g, () => {
        if (parameters.pathVariables && parameters.pathVariables.length > 0) {
          return parameters.pathVariables.shift() ?? '';
        } else {
          return '';
        }
      });
    }

    let request = this.getDefaultParameters(parameters.body);
    console.log("Api request ===> " + url + " " + JSON.stringify(request));
    let response: Observable<HttpResponse<RESPONSE>>;
    if ( parameters.requestType === RequestType.POST ||
      parameters.requestType === RequestType.DELETE ||
      parameters.requestType === RequestType.UPDATE) {
      response = this.getRequestHeaders({
        isPostRequest: true,
      }).pipe(
        mergeMap((headers) => {
          if (parameters.requestType === RequestType.POST) {
            return this.httpClient.post<RESPONSE>(url!, request, {
              observe: "response",
              headers: headers!,
              context: undefined,
            });
          } else if (parameters.requestType === RequestType.UPDATE) {
            return this.httpClient.put<RESPONSE>(url!, request, { // Modified this line
              observe: "response",
              headers: headers!,
              context: undefined,
            });
          } else if (parameters.requestType === RequestType.DELETE) {
            return this.httpClient.delete<RESPONSE>(url!, {
              observe: "response",
              headers: headers!,
              context: undefined,
            });
          } else {
            throw "";
          }
        })
      );
    } else {
      const queryParameters = new URLSearchParams(request).toString();
      let urlWithParameters: string = `${url}?${queryParameters}`;
      response = this.getRequestHeaders({
        isPostRequest: false,
      }).pipe(
        mergeMap((headers) =>
          this.httpClient.get<RESPONSE>(urlWithParameters, {
            observe: "response",
            headers: headers,
            context: undefined,
          })
        )
      );
    }

    return response.pipe(
      map((response) => {
        return this.handleResponse(response);
      }),
      catchError(this.handleError<RESPONSE>(parameters))
    );
  }

  private getRequestHeaders(parameters: {
    isPostRequest: boolean;
  }): Observable<HttpHeaders> {
    let headers: HttpHeaders;

    if (this.jwtToken !== null && this.jwtToken !== undefined) {
      headers = new HttpHeaders({
        Authorization: `Bearer ${this.jwtToken}`,
      });
    } else {
      headers = new HttpHeaders({});
    }

    if (parameters.isPostRequest) {
      headers.set("Content-Type", "application/json");
    }
    return of(headers)
  }

  private getDefaultParameters(body: any): any {
    return {
      ...body,
      lang: "en",
    };
  }

  private handleResponse<RESPONSE>(response: HttpResponse<RESPONSE>): RESPONSE {
    let statusCode = response.status;
    if (
      statusCode == ResponseStatusCode.OK ||
      statusCode == ResponseStatusCode.CREATED ||
      statusCode == ResponseStatusCode.ACCEPTED
    ) {
      if (response.body != null) {
        console.log("Api response ===> " + response.url, response.body);
        return response.body;
      } else return Object();
    } else {
      throw "Unknown Error";
    }
  }

  private handleError<RESPONSE>(parameters: {
    contentType?: string
    pathVariables?: string[]
    body?: any
  }): (err: any, caught: Observable<RESPONSE>) => Observable<any> {
    return (error, _) => {
      console.log(error.error);
      let statusCode = error.status;
      if (
        statusCode == ResponseStatusCode.OK ||
        statusCode == ResponseStatusCode.CREATED ||
        statusCode == ResponseStatusCode.ACCEPTED
      ) {
        let body = error.error.text;
        return of(body);
      } else {
        const processedError = this.errorHandlerService.handleApiError(error);

        // If unauthorized, clear the JWT token for the user to login back again.
        if (processedError === ValidationErrorType.UNAUTHORIZED)
          this.clearJWTToken();
        // Rethrow the error for the component to handle
        return throwError(() => processedError);
      }
    };
  }

  private getToken() {
    if (this.jwtToken == undefined) {
      if (localStorage.getItem("token") && localStorage.getItem("tokenType") && localStorage.getItem("tokenExpiry") && (localStorage.getItem("user") || localStorage.getItem("customer"))) {
        this.jwtToken = {
          accessToken: localStorage.getItem("token")!,
          tokenType: localStorage.getItem("tokenType")!,
          expiresIn: parseInt(localStorage.getItem("tokenExpiry")!),
          user: JSON.parse(localStorage.getItem("user")!),
          customer: localStorage.getItem('customer') !== null ? JSON.parse(localStorage.getItem('customer')!) : null
        }
      }
    }
  }

  saveJWTToken(jwtToken: JWTToken) {
    this.clearJWTToken()
    console.log("Received jwt token ", jwtToken)
    this.jwtToken = jwtToken
    localStorage.setItem("token", this.jwtToken.accessToken)
    localStorage.setItem("tokenExpiry", Math.floor(new Date().getTime() / 1000) + this.jwtToken.accessToken)
    localStorage.setItem("tokenType", this.jwtToken.tokenType)
    if (this.jwtToken != null && this.jwtToken.user != null)
      localStorage.setItem("user", JSON.stringify(this.jwtToken.user));
    else
      localStorage.removeItem("user")

    if (this.jwtToken != null && this.jwtToken.customer != null) {
      localStorage.setItem("customer", JSON.stringify(this.jwtToken.customer));
    } else {
      localStorage.removeItem("customer")
    }


    clearTimeout(this.refreshTokenTimer) // Not sure if this is needed.
    let timerTickIn = (this.jwtToken.expiresIn * 0.9)
    console.log("refreshing token in " + timerTickIn)
    this.refreshTokenTimer = setTimeout(() => {
      console.log("refreshing token now")
      this.refreshToken().subscribe()
    }, timerTickIn * 1000);

  }

  clearJWTToken() {
    console.log("clearing token now")
    this.jwtToken = null
    localStorage.clear() // TEST
    // TODO remove mobile number from local storage
  }

  refreshToken() {
    return this.httpClient.post<JWTToken>(this.baseUrl + '/' + this.version + '/account/refreshToken', "")
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 401) {
            console.log("Unauthorized response.")
          } else if (error.status === 422) {
            console.log("Validation error..")
          }
          // Return an ObservableInput
          // return Observable;
          // Return an observable with a user-facing error message.
          // return throwError(() => new Error('Something bad happened; please try again later.'));
          return throwError(() => error)
        }),
        map((response: JWTToken) => {
            this.saveJWTToken(response)
            return response
          }
        )
      )
  }

  getJwt(): JWTToken | null {
    return this.jwtToken
  }
}
